import { AfterViewChecked, Component, ElementRef, OnChanges, OnInit, ViewChild, input } from '@angular/core';
import {
    ChartDisplay,
    ChartResultElement,
    ChartSeries,
    View,
} from '@r1-compass-library/models/reporting/chart-data.model';
import { Color, NgxChartsModule } from '@swimlane/ngx-charts';
import { DateTime } from 'luxon';

@Component({
    selector: 'r1-compass-lib-report-chart',
    imports: [NgxChartsModule],
    templateUrl: './report-chart.component.html',
    styleUrl: './report-chart.component.scss',
})
export class ReportChartComponent implements OnInit, OnChanges, AfterViewChecked {
    readonly chartData = input<ChartResultElement[]>([]);

    readonly chartView = input.required<View>();

    @ViewChild('chartContainer')
    chartContainer!: ElementRef<HTMLElement>;

    chartDisplayData: ChartResultElement[] = [];
    chartColors = { domain: ['#93d500', '#1174b6', '#ff9933', '#f3b88c'] } as Color;
    timeIncrementWindows: ChartResultElement[] = [];
    view: [number, number] = [850, 400];
    showXAxis = true;
    showYAxis = true;
    showXAxisLabel = true;
    showYAxisLabel = true;
    xAxisLabel = 'Time';
    yAxisLabel = 'Count';
    gradient = false;
    showLegend = true;
    animations = true;
    xAxisTicks: string[] = [];

    ngOnInit() {
        const chartView = this.chartView();
        if (chartView) {
            const chartDisplay = this.generateNewTimeIncrementWindows(chartView);
            this.chartDisplayData = chartDisplay.data;
            this.timeIncrementWindows = chartDisplay.data;
            this.xAxisTicks = chartDisplay.axisMarkers;
        }
    }

    ngAfterViewChecked() {
        this.view[0] = this.chartContainer.nativeElement.offsetWidth;
    }

    ngOnChanges() {
        const chartData = this.chartData();
        if (this.chartView() && chartData && this.timeIncrementWindows) {
            if (chartData.length > 0 && this.timeIncrementWindows.length > 0) {
                const timeIncrementWindows = JSON.parse(JSON.stringify(this.timeIncrementWindows));
                this.chartDisplayData = this.mergeDataIntoWindows(chartData, timeIncrementWindows);
            } else {
                this.chartDisplayData = this.timeIncrementWindows;
            }
        }
    }

    private generateNewTimeIncrementWindows(view: View): ChartDisplay {
        const chartDisplay: ChartDisplay = new ChartDisplay();
        const chartDisplayData: ChartResultElement[] = [];
        const axisMarkers: string[] = [];
        const numWindows = view.numberOfUnits * view.incrementsPerUnit;
        let i;
        let start = this.generateStartTime();
        for (i = 0; i < numWindows; i++) {
            chartDisplayData.push(this.generateChartDataObj(start));
            if (i % view.incrementsPerUnit === 0) {
                axisMarkers.push(DateTime.fromISO(start).toFormat('HH:mm'));
            }
            start = DateTime.fromISO(start)
                .plus({ [view.incrementUnit]: view.incrementSize })
                .toISO()!;
        }
        chartDisplay.data = chartDisplayData;
        chartDisplay.axisMarkers = axisMarkers;
        return chartDisplay;
    }

    private generateStartTime(): string {
        return DateTime.now().set({ hour: 0 }).set({ minute: 0 }).set({ second: 0 }).set({ millisecond: 0 }).toISO()!;
    }

    private generateChartDataObj(startTime: string): ChartResultElement {
        const newResultObj: ChartResultElement = new ChartResultElement();
        newResultObj.name = DateTime.fromISO(startTime).toFormat('HH:mm');
        newResultObj.series = [];
        return newResultObj;
    }

    private mergeDataIntoWindows(
        reportData: ChartResultElement[],
        timeWindows: ChartResultElement[]
    ): ChartResultElement[] {
        const clonedTimeWindows = [...timeWindows];
        reportData.forEach(chartElement => {
            const chartElementTime = DateTime.fromISO(chartElement.name).toFormat('HH:mm');
            clonedTimeWindows.forEach((window, i) => {
                if (i === timeWindows.length - 1) {
                    if (chartElementTime >= timeWindows[i].name) {
                        this.modifyTimeWindow(window, chartElement);
                    }
                } else {
                    if (chartElementTime >= timeWindows[i].name && chartElementTime < timeWindows[i + 1].name) {
                        this.modifyTimeWindow(window, chartElement);
                    }
                }
            });
        });
        return clonedTimeWindows;
    }

    private modifyTimeWindow(window: ChartResultElement, chartElement: ChartResultElement) {
        if (window.series.length === 0) {
            window.series = chartElement.series;
        } else {
            window.series = this.mergeSeriesData(chartElement.series, window.series);
        }
    }

    private mergeSeriesData(newSeries: ChartSeries[], existingSeries: ChartSeries[]): ChartSeries[] {
        existingSeries.forEach(seriesElement => {
            const newSeriesMatch = newSeries.find(element => element.name === seriesElement.name);
            if (newSeriesMatch) {
                seriesElement.value = seriesElement.value + newSeriesMatch.value;
            }
        });
        return existingSeries;
    }
}
